"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { LucideIcon } from "lucide-react";
import React from "react";
import { Button } from "@/components/ui/button";
import { CopyToClipboardButton } from "@/components/ui/copy-to-clipboard-button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  TruncateTooltip,
  TooltipContent as TruncateTooltipContent,
  TooltipTrigger as TruncateTooltipTrigger,
} from "@/components/ui/truncate-tooltip";
import { cn } from "@/lib/utils";

export type InteractiveCellAction = {
  icon: LucideIcon;
  onClick: () => void;
  label: React.ReactNode;
  ariaLabel?: string;
};

export type InteractiveCellProps = {
  /** 表示するテキスト */
  text: string;
  /** セルがクリック可能な場合のハンドラー */
  onClick?: () => void;
  /** アクションボタンの配列 */
  actions?: InteractiveCellAction[];
  /** テキストのクラス名 */
  textClassName?: string;
  /** セル全体のクラス名 */
  className?: string;
  /** コピー機能を有効にするか */
  enableCopy?: boolean;
  /** TruncateTooltipを使用して長いテキストを省略するか */
  enableTruncate?: boolean;
};

export const InteractiveCell = React.memo<InteractiveCellProps>(
  ({
    text,
    onClick,
    actions = [],
    textClassName,
    className,
    enableCopy = true,
    enableTruncate = true,
  }) => {
    const { t } = useLingui();

    // actions をそのまま使用
    const allActions = actions;

    const textContent = enableTruncate ? (
      <TruncateTooltip delayDuration={0}>
        <TruncateTooltipTrigger asChild>
          <span
            className={cn(
              "block break-all line-clamp-2 overflow-hidden",
              textClassName,
              onClick && "hover:underline cursor-pointer",
            )}
            style={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            onClick={onClick}
          >
            {text}
          </span>
        </TruncateTooltipTrigger>
        <TruncateTooltipContent>
          <p>{text}</p>
        </TruncateTooltipContent>
      </TruncateTooltip>
    ) : (
      <span
        className={cn(
          "truncate",
          textClassName,
          onClick && "hover:underline cursor-pointer",
        )}
        title={text}
        onClick={onClick}
      >
        {text}
      </span>
    );

    return (
      <div
        className={cn(
          "group relative flex items-center whitespace-normal",
          className,
        )}
      >
        <div className="flex-1 min-w-0">{textContent}</div>

        {/* ホバー時に表示されるアクションボタン（絶対配置でオーバーレイ） */}
        {(allActions.length > 0 || enableCopy) && (
          <div
            className={cn(
              "absolute right-0 top-1/2 -translate-y-1/2 z-10",
              "flex items-center gap-0.5 transition-all duration-200 ease-in-out",
              "bg-background/95 backdrop-blur-sm border border-border/50 rounded-md shadow-md px-1 py-0.5",
              "opacity-0 pointer-events-none",
              "group-hover:opacity-100 group-hover:pointer-events-auto",
            )}
          >
            {allActions.map((action, index) => (
              <Tooltip
                key={index}
                delayDuration={0}
                disableHoverableContent={true}
              >
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick();
                      // ボタンのフォーカスを外す
                      e.currentTarget.blur();
                    }}
                    className="h-6 w-6 hover:bg-muted"
                    aria-label={action.ariaLabel}
                  >
                    <action.icon size={12} className="pointer-events-none" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{action.label}</TooltipContent>
              </Tooltip>
            ))}

            {enableCopy && (
              <CopyToClipboardButton
                textToCopy={text}
                variant="ghost"
                size="icon-sm"
                className="h-6 w-6 hover:bg-muted"
                tooltipText={t(msg`Copy to clipboard`)}
                disableHoverableContent={true}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);

InteractiveCell.displayName = "InteractiveCell";
